import { unsafeCoerce } from 'fp-ts/lib/function';
import { useState } from 'react';
import { TrackingRegion } from '../../../common/AnalyticsContext/TrackingData';
import { useFavourites } from '../../../common/FavouritesProvider/FavouritesProvider';
import { AppointmentLightBoxModal } from '../../../common/components/AppointmentLightBoxModal/AppointmentLightBoxModal';
import { Heading } from '../../../common/components/Typography/Heading/Heading';
import { Col } from '../../../common/components/layout/FlexLayout';
import { PageSection } from '../../../common/components/layout/PageSection/PageSection';
import { useToggle } from '../../../common/hooks/useToggle';
import { Api3Favourite } from '../../../common/schema/api3/types/Api3Favourite';
import { LimitedSet } from '../../../common/util/limitedSet';
import { MyCareTeamPracticeCard, MyCareTeamPractitionerCard } from './MyCareTeamCard';
import {
  MyCareTeamRow,
  MyCareTeamSectionEditButton,
  MyCareTeamSectionHeader,
  MyCareTeamSectionIcon,
  MyCareTeamSectionTitle,
  MyCareTeamCol,
} from './MyCareTeamSection.styles';
import { MyCareTeamPractitioner, MyCareTeamPractice } from './types';

const CARE_TEAM_CARD_LIMIT = 2;

export const MyCareTeamSection = () => {
  const { favourites } = useFavourites();
  const [modalOpen, toggleModalOpen] = useToggle(false);
  const [appointmentUrl, setAppointmentUrl] = useState('');

  if (!favourites.length) return null;

  const openAppointmentModal = (url: string) => {
    setAppointmentUrl(url);
    toggleModalOpen();
  };

  const practitioners = new LimitedSet(
    unsafeCoerce<Api3Favourite[], MyCareTeamPractitioner[]>(
      favourites.filter(f => !!f.practitioner),
    ),
    CARE_TEAM_CARD_LIMIT,
  );

  const practices = new LimitedSet(
    unsafeCoerce<Api3Favourite[], MyCareTeamPractice[]>(favourites.filter(f => !!f.practice)),
    CARE_TEAM_CARD_LIMIT,
  );

  return (
    <>
      <PageSection trackingRegionId={TrackingRegion.MY_CARE_TEAM}>
        <MyCareTeamSectionHeader>
          <Col>
            <MyCareTeamSectionTitle small>
              <MyCareTeamSectionIcon color="#394F51" /> My Care Team™
            </MyCareTeamSectionTitle>
          </Col>
          <Col>
            <MyCareTeamSectionEditButton
              to="/my-account/favourites"
              options={{ keyline: true, small: true }}
            >
              Edit My Care Team™
            </MyCareTeamSectionEditButton>
          </Col>
        </MyCareTeamSectionHeader>
        {practitioners.totalCount > 0 && (
          <>
            <Heading h3 small>
              Practitioners
            </Heading>
            <MyCareTeamRow>
              {practitioners.limitedSet.map((p, i) => (
                <MyCareTeamCol key={i}>
                  <MyCareTeamPractitionerCard {...p} openAppointmentsModal={openAppointmentModal} />
                </MyCareTeamCol>
              ))}
              {practitioners.totalCount > CARE_TEAM_CARD_LIMIT && (
                <Col>
                  <MyCareTeamSectionEditButton
                    to="/my-account/favourites"
                    options={{ keyline: true, small: true }}
                  >
                    Show all {practitioners.totalCount} practitioners
                  </MyCareTeamSectionEditButton>
                </Col>
              )}
            </MyCareTeamRow>
          </>
        )}
        {practices.totalCount > 0 && (
          <>
            <Heading h3 small>
              Practices
            </Heading>
            <MyCareTeamRow>
              {practices.limitedSet.map((p, i) => (
                <MyCareTeamCol key={i}>
                  <MyCareTeamPracticeCard {...p} openAppointmentsModal={openAppointmentModal} />
                </MyCareTeamCol>
              ))}
              {practices.totalCount > CARE_TEAM_CARD_LIMIT && (
                <Col>
                  <MyCareTeamSectionEditButton
                    to="/my-account/favourites"
                    options={{ keyline: true, small: true }}
                  >
                    Show all {practices.totalCount} practices
                  </MyCareTeamSectionEditButton>
                </Col>
              )}
            </MyCareTeamRow>
          </>
        )}
      </PageSection>
      <AppointmentLightBoxModal
        isOpen={modalOpen}
        iframeUrl={appointmentUrl}
        onRequestClose={toggleModalOpen}
      />
    </>
  );
};
